import {
  useRef,
  useEffect,
  useCallback,
  useState,
  type RefObject,
} from 'react';

export interface VideoSource {
  url: string;
  // 'file' = riproduzione diretta nativa (es. audio mp3/m4a), senza Shaka —
  // Shaka è pensato per manifest adattivi (HLS/DASH), non per file singoli.
  format: 'hls' | 'dash' | 'mp4' | 'file';
}

export interface ShakaPlayerState {
  buffering: boolean;
  error: string | null;
  isReady: boolean;
}

interface UseShakaPlayerOptions {
  videoRef: RefObject<HTMLVideoElement | null>;
  source: VideoSource | null;
  authToken?: string;
  onTimeUpdate?: (currentTime: number) => void;
  onEnded?: () => void;
  onError?: (message: string) => void;
}

export function useShakaPlayer({
  videoRef,
  source,
  authToken,
  onTimeUpdate,
  onEnded,
  onError,
}: UseShakaPlayerOptions): ShakaPlayerState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerRef = useRef<any>(null);
  const [state, setState] = useState<ShakaPlayerState>({
    buffering: false,
    error: null,
    isReady: false,
  });

  // Callback refs stabili — evitano stale closure senza aggiungere dipendenze all'effect
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onEndedRef = useRef(onEnded);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);
  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const handleError = useCallback((shakaError: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const msg =
      (shakaError as any)?.message ?? 'Errore durante la riproduzione';
    setState((prev) => ({ ...prev, error: msg, isReady: false }));
    onErrorRef.current?.(msg);
  }, []);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !source) return;

    const currentSource = source;

    let destroyed = false;

    async function initPlayer() {
      if (!videoEl) return;
      try {
        // File singolo (es. audio mp3/m4a di una lezione): il tag <video>
        // lo riproduce nativamente, Shaka servirebbe solo a fallire nel
        // tentativo di interpretarlo come manifest HLS/DASH.
        if (currentSource.format === 'file') {
          if (playerRef.current) {
            await playerRef.current.destroy();
            playerRef.current = null;
          }
          videoEl.src = currentSource.url;
          videoEl.load();
          if (!destroyed)
            setState({ buffering: false, error: null, isReady: true });
          return;
        }

        const shaka = (await import('shaka-player')) as any;
        if (destroyed) return;

        shaka.polyfill.installAll();

        if (!shaka.Player.isBrowserSupported()) {
          handleError(new Error('Browser non supportato da Shaka Player'));
          return;
        }

        if (playerRef.current) await playerRef.current.destroy();
        playerRef.current = null;

        // Piccolo delay per assicurare che il video element sia pronto dopo il destroy
        await new Promise((resolve) => setTimeout(resolve, 50));

        if (destroyed) return;

        const player = new shaka.Player();
        await player.attach(videoEl);
        playerRef.current = player;

        if (authToken) {
          player
            .getNetworkingEngine()
            .registerRequestFilter((_type: unknown, request: any) => {
              const uri: string = request.uris?.[0] ?? '';

              const isOwnServer =
                uri.startsWith('/') ||
                uri.includes('localhost') ||
                uri.includes(window.location.hostname);

              if (isOwnServer) {
                request.headers['Authorization'] = `Bearer ${authToken}`;
              }
            });
        }

        player.addEventListener('error', (event: any) =>
          handleError(event.detail)
        );
        player.addEventListener('buffering', (event: any) => {
          setState((prev) => ({ ...prev, buffering: event.buffering }));
        });

        const MIME_TYPES: Record<string, string> = {
          hls: 'application/x-mpegURL',
          dash: 'application/dash+xml',
          mp4: 'video/mp4',
        };
        await player.load(
          currentSource.url,
          undefined,
          MIME_TYPES[currentSource.format]
        );

        if (destroyed) return;
        setState({ buffering: false, error: null, isReady: true });
      } catch (err) {
        if (!destroyed) handleError(err);
      }
    }

    const handleTimeUpdate = () => {
      // videoEl catturato dalla closure — sempre non-null qui
      onTimeUpdateRef.current?.(videoEl.currentTime);
    };
    const handleEnded = () => {
      onEndedRef.current?.();
    };
    // Solo per il caso 'file' — Shaka ha già un suo listener 'error' registrato
    // sopra in initPlayer() e non passa mai da qui.
    const handleNativeError = () => {
      if (currentSource.format === 'file') handleError(videoEl.error);
    };

    videoEl.addEventListener('timeupdate', handleTimeUpdate);
    videoEl.addEventListener('ended', handleEnded);
    videoEl.addEventListener('error', handleNativeError);

    setState({ buffering: false, error: null, isReady: false });
    initPlayer();

    return () => {
      destroyed = true;
      videoEl.removeEventListener('timeupdate', handleTimeUpdate);
      videoEl.removeEventListener('ended', handleEnded);
      videoEl.removeEventListener('error', handleNativeError);
      if (playerRef.current) {
        playerRef.current.destroy().catch(() => {
          /* silenzioso */
        });
        playerRef.current = null;
      }
    };
    // source.url come dipendenza: re-init solo al cambio lezione, non ad ogni render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.url, authToken, handleError]);
  // NOTA: videoRef intenzionalmente escluso — è una ref stabile, non cambia mai

  return state;
}
